/**
 * useFileUpload — Upload files to Supabase Storage.
 *
 * Provides file selection, preview, and upload to the
 * `console-uploads` bucket in Supabase Storage.
 *
 * Files are stored under {tenant_id}/{timestamp}-{sanitized_filename}
 * to ensure multi-tenant isolation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import { getSupabase } from '../lib/supabase';

/** Resolve the Supabase client; returns null if runtime config hasn't loaded. */
function tryGetSupabase(): SupabaseClient | null {
  try {
    return getSupabase();
  } catch {
    return null;
  }
}

const BUCKET = 'console-uploads';
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ACCEPTED_TYPES = [
  'image/*',
  'application/pdf',
  '.doc',
  '.docx',
  '.txt',
  '.csv',
  '.xlsx',
].join(',');

interface PendingFile {
  file: File;
  preview: string | null; // Object URL for images, null for docs
}

interface UploadResult {
  url: string;
  name: string;
  size: number;
  type: string;
}

interface UseFileUploadReturn {
  /** Open the native file picker */
  selectFile: () => void;
  /** Currently selected file awaiting upload */
  pendingFile: PendingFile | null;
  /** Clear the pending file */
  clearPendingFile: () => void;
  /** Upload a file to Supabase Storage and return its public URL */
  uploadFile: (file: File) => Promise<UploadResult | null>;
  /** Whether an upload is in progress */
  isUploading: boolean;
  /** Error message if something went wrong */
  error: string | null;
  /** Accepted MIME types for the file picker */
  acceptedTypes: string;
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .toLowerCase();
}

export function useFileUpload(): UseFileUploadReturn {
  const { user } = useAuth();
  const [pendingFile, setPendingFile] = useState<PendingFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Create a hidden file input lazily, track it for cleanup
  const getInput = useCallback(() => {
    if (!inputRef.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = ACCEPTED_TYPES;
      input.style.display = 'none';
      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE) {
          setError(`File too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
          return;
        }

        setError(null);
        const preview = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null;
        setPendingFile({ file, preview });

        // Reset input so the same file can be re-selected
        input.value = '';
      });
      document.body.appendChild(input);
      inputRef.current = input;
    }
    return inputRef.current;
  }, []);

  // Clean up the hidden input element on unmount
  useEffect(() => {
    return () => {
      if (inputRef.current) {
        inputRef.current.remove();
        inputRef.current = null;
      }
    };
  }, []);

  const selectFile = useCallback(() => {
    getInput().click();
  }, [getInput]);

  const clearPendingFile = useCallback(() => {
    if (pendingFile?.preview) {
      URL.revokeObjectURL(pendingFile.preview);
    }
    setPendingFile(null);
    setError(null);
  }, [pendingFile]);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      const supabase = tryGetSupabase();
      if (!supabase) {
        setError('Supabase client not available');
        return null;
      }

      const tenantId = user?.organization_id || 'unknown';
      const timestamp = Date.now();
      const safeName = sanitizeFilename(file.name);
      const path = `${tenantId}/${timestamp}-${safeName}`;

      setError(null);
      setIsUploading(true);

      try {
        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          throw new Error(uploadError.message);
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from(BUCKET)
          .getPublicUrl(path);

        return {
          url: urlData.publicUrl,
          name: file.name,
          size: file.size,
          type: file.type,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setError(message);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [user?.organization_id]
  );

  return {
    selectFile,
    pendingFile,
    clearPendingFile,
    uploadFile,
    isUploading,
    error,
    acceptedTypes: ACCEPTED_TYPES,
  };
}
